import React from 'react';
import { CheckIcon, XMarkIcon, SparklesIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export interface FormSuggestion {
  field: string;
  value: string;
  confidence: 'low' | 'medium' | 'high';
  reasoning?: string;
}

export interface FormSuggestionsResponse {
  suggestions: FormSuggestion[];
  overallConfidence: 'low' | 'medium' | 'high';
  analysisNotes?: string;
}

interface SuggestionFieldProps {
  suggestion?: FormSuggestion;
  isAccepted: boolean;
  isRejected: boolean;
  onAccept: () => void;
  onReject: () => void;
  children: React.ReactNode;
}

interface SuggestionsBannerProps {
  suggestions: FormSuggestion[];
  overallConfidence: 'low' | 'medium' | 'high';
  analysisNotes?: string;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  acceptedCount: number;
  totalCount: number;
}

interface ConfidenceIndicatorProps {
  confidence: 'low' | 'medium' | 'high';
  size?: 'sm' | 'md';
}

interface TooltipProps {
  content: string;
  children: React.ReactNode;
}

// Simple tooltip component
function Tooltip({ content, children }: TooltipProps) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
        {content}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
}

// Confidence indicator component
function ConfidenceIndicator({ confidence, size = 'md' }: ConfidenceIndicatorProps) {
  const getConfidenceColor = (conf: string) => {
    switch (conf) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1';

  return (
    <Tooltip content={`AI confidence: ${confidence}. This indicates how certain the AI is about this suggestion.`}>
      <span className={`inline-flex items-center rounded-full font-medium ${getConfidenceColor(confidence)} ${sizeClasses}`}>
        <SparklesIcon className="w-3 h-3 mr-1" />
        {confidence}
      </span>
    </Tooltip>
  );
}

// Individual suggestion field wrapper
export function SuggestionField({ 
  suggestion, 
  isAccepted, 
  isRejected, 
  onAccept, 
  onReject, 
  children 
}: SuggestionFieldProps) {
  // Don't show suggestion UI if there's no suggestion
  if (!suggestion) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      {children}
      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <SparklesIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm font-medium text-blue-900">AI Suggestion</span>
              <ConfidenceIndicator confidence={suggestion.confidence} />
            </div>
            <p className="text-sm text-blue-800 break-words">{suggestion.value}</p>
            {suggestion.reasoning && (
              <p className="text-xs text-blue-600 mt-1 italic">{suggestion.reasoning}</p>
            )}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            {!isAccepted && !isRejected && (
              <>
                <button
                  type="button"
                  onClick={onAccept}
                  className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                  title="Accept suggestion"
                >
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={onReject}
                  className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                  title="Reject suggestion"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </>
            )}
            {isAccepted && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckIcon className="w-4 h-4" />
                <span className="text-xs">Accepted</span>
              </div>
            )}
            {isRejected && (
              <div className="flex items-center gap-1 text-red-600">
                <XMarkIcon className="w-4 h-4" />
                <span className="text-xs">Rejected</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Banner component for overall suggestions management
export function SuggestionsBanner({ 
  suggestions, 
  overallConfidence, 
  analysisNotes,
  onAcceptAll, 
  onRejectAll,
  acceptedCount,
  totalCount
}: SuggestionsBannerProps) {
  if (suggestions.length === 0) return null;

  const allAccepted = acceptedCount === totalCount;
  const someAccepted = acceptedCount > 0;

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <SparklesIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-900">
              AI Form Suggestions
            </h3>
            <ConfidenceIndicator confidence={overallConfidence} />
          </div>
          
          <p className="text-sm text-blue-800 mb-2">
            Found {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} based on your uploaded media.
            {someAccepted && (
              <span className="ml-1 font-medium">
                ({acceptedCount}/{totalCount} accepted)
              </span>
            )}
          </p>
          
          {analysisNotes && (
            <div className="flex items-start gap-2 text-xs text-blue-700">
              <InformationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{analysisNotes}</span>
            </div>
          )}
        </div>
        
        <div className="flex gap-2 flex-shrink-0">
          {!allAccepted && (
            <Tooltip content="Accept all AI suggestions and apply them to the form">
              <button
                type="button"
                onClick={onAcceptAll}
                className="btn btn-success btn-sm flex items-center gap-1"
              >
                <CheckIcon className="w-4 h-4" />
                Accept All
              </button>
            </Tooltip>
          )}
          
          <Tooltip content="Reject all AI suggestions and hide them">
            <button
              type="button"
              onClick={onRejectAll}
              className="btn btn-neutral btn-sm flex items-center gap-1"
            >
              <XMarkIcon className="w-4 h-4" />
              {allAccepted ? 'Clear All' : 'Reject All'}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

// Loading state component for AI analysis
interface AIAnalysisLoadingProps {
  progress?: number;
  status?: string;
}

export function AIAnalysisLoading({ progress, status = 'Analyzing media with AI...' }: AIAnalysisLoadingProps) {
  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="animate-spin">
          <SparklesIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-blue-900 mb-1">
            {status}
          </div>
          {progress !== undefined && (
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          )}
          {progress === undefined && (
            <div className="text-xs text-blue-700">
              This may take a few moments...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Error state component for AI analysis
interface AIAnalysisErrorProps {
  error: string;
  onRetry?: () => void;
  onDismiss: () => void;
}

export function AIAnalysisError({ error, onRetry, onDismiss }: AIAnalysisErrorProps) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-start gap-3">
        <XMarkIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-red-900 mb-1">
            AI Analysis Failed
          </div>
          <div className="text-sm text-red-800 mb-3">
            {error}
          </div>
          <div className="flex gap-2">
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="btn btn-error btn-sm"
              >
                Try Again
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="btn btn-neutral btn-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 